import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import { requiredImageFromCMSSchema, richTextFromCMSSchema } from '../../cms';
import { full_ingredients_modal } from '../../cms/builders';
import dogInBoxEmpty from '../../images/dog-in-box-empty.png';
import {
  planLoaderMessages as defaultPlanLoaderMessages,
  planLoaderButtonText as defaultPlanLoaderButtonText,
} from '../constants';

const itemSchema = yup.object({
  title: yup.string().required(),
  label: yup.string().required(),
  benefit: yup.string().optional(),
  callout: yup.string().optional(),
  value: yup.string().required(),
  description: richTextFromCMSSchema,
  img: requiredImageFromCMSSchema,
  unavailableText: yup.string(),
  priceSymbol: yup.string().optional(),
  showMoreDetails: yup.bool(),
  pricingInfo: yup.object({
    price: yup.string(),
    discount: yup.string(),
  }),
  level: yup.string(),
});

export const planSelectionSchema = yup
  .object({
    planSelectionInfo: yup
      .object({
        headline: yup.string().required(),
        subheadline: yup.string().required(),
        summaryHeadline: yup.string().required(),
        image: requiredImageFromCMSSchema,
        items: yup
          .array(
            itemSchema
              .from('display_name', 'title')
              .from('slug', 'value')
              .from('image', 'img')
              .from('unavailable_text', 'unavailableText')
              .from('show_more_details', 'showMoreDetails'),
          )
          .required(),
        collapsedItems: yup
          .array(
            itemSchema
              .from('display_name', 'title')
              .from('slug', 'value')
              .from('image', 'img')
              .from('unavailable_text', 'unavailableText')
              .from('show_more_details', 'showMoreDetails'),
          )
          .required(),
        mobileCtaText: yup.string().required(),
        desktopCtaText: yup.string().required(),
        modals: yup.array(full_ingredients_modal.schema).required(),
        notSelectableText: yup.string(),
      })
      .from('starter_plan_summary_headline', 'summaryHeadline')
      .from('starter_plan_icon', 'image')
      .from('desktop_cta_text', 'desktopCtaText')
      .from('mobile_cta_text', 'mobileCtaText')
      .from('not_selectable_text', 'notSelectableText'),
    noRecipesInfo: yup.object({}).required(),
    body: yup.array(yup.mixed()),
  })
  .transform(({ body, ...value }) => {
    let { value: experimentValue } = useExperiment(
      value.recipe_selector?.[0]?.test_name ?? null,
    );
    experimentValue = experimentValue || 'control';
    let productSelectorCards = value.recipe_selector;
    if (productSelectorCards?.[0].component === 'flagship_ab_test') {
      const experimentVariation = productSelectorCards[0].variants.find(
        variant => variant.variant_name === experimentValue,
      );
      productSelectorCards = experimentVariation?.blocks ?? [];
    }
    return {
      // TODO(James) integrate the rest of this with the CMS
      planSelectionInfo: {
        ...value,
        showMoreDetails: value.show_more_details,
        ctaText: 'Continue',
        items: productSelectorCards
          .filter(item => !item.hidden)
          .map(recipe => ({
            ...recipe,
            priceSymbol: recipe.price_symbol,
            pricingInfo: {
              price: recipe.price,
              discount: recipe.discount,
            },
          })),
        collapsedItems: productSelectorCards
          .filter(item => item.hidden)
          .map(recipe => ({
            ...recipe,
            priceSymbol: recipe.price_symbol,
            pricingInfo: {
              price: recipe.price,
              discount: recipe.discount,
            },
          })),
      },
      noRecipesInfo: {
        headline: 'Doggone it!',
        subheadline:
          "We're unable to recommend a recipe for {{pupName}} at this time due to one or more allergies.",
        messagePreText: "You told us that {{pupName}} can't have:",
        messagePostText:
          "Double check this is accurate and adjust {{pupName}}'s allergy profile to see available recipes",
        buttonText: "Edit {{pupName}}'s allergies",
        image: {
          src: dogInBoxEmpty.src,
          alt: 'dog in empty box',
        },
      },
      planLoaderMessages: defaultPlanLoaderMessages,
      planLoaderButtonText: defaultPlanLoaderButtonText,
      body,
    };
  });

export const recommendedPlanSchema = yup
  .object({
    noRecipesInfo: yup.object({}).required(),
  })
  .transform(() => ({
    noRecipesInfo: {
      headline: 'Doggone it!',
      subheadline:
        "We're unable to recommend a recipe for {{pupName}} at this time due to one or more allergies.",
      messagePreText: "You told us that {{pupName}} can't have:",
      messagePostText:
        "Double check this is accurate and adjust {{pupName}}'s allergy profile to see available recipes",
      buttonText: "Edit {{pupName}}'s allergies",
      image: {
        src: dogInBoxEmpty.src,
        alt: 'dog in empty box',
      },
    },
    planLoaderMessages: defaultPlanLoaderMessages,
    planLoaderButtonText: defaultPlanLoaderButtonText,
  }));
